import { FormState, ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { isNil } from 'ramda';
import React, { Children, ReactElement, ReactNode, useState } from 'react';
import { Form, FormRenderProps, FormSpy } from 'react-final-form';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { isArray, merge, mergeWith } from 'lodash';
import { ImportJson } from '~admin/view/components/ui/import-json.component';
import { Box } from '~common/components/box.component';
import { ButtonType } from '~common/components/button/button.helper';
import { FormButton } from '~common/components/button/form-button.component';
import { Column } from '~common/components/column.component';
import { AnyTask } from '~common/helper/mobx-tasks';
import { ArrowLeftButton } from '~web/view/components/ui/button/arrow-left-button.component';
import { ArrowRightButton } from '~web/view/components/ui/button/arrow-right-button.component';
import { isNilOrEmpty } from '~common/helper/global';

export interface WizardPageProps<T> {
  validate?: (values: T) => ValidationErrors | undefined;
  formRenderProps?: FormRenderProps<T>;
}

interface WizardState {
  page: number;
}

interface Props<T> {
  onSubmit: (values: T) => void;
  submitButtonText: string;
  initialValues?: T;
  onFormChange?: (formState: FormState<T>) => void;
  task?: AnyTask;
  allowImportJson?: boolean;
}

/**
 * Source: https://final-form.org/docs/react-final-form/examples/wizard
 */
export const Wizard = <T extends object>(
  props: Props<T> & {
    children?: ReactNode | ReactNode[];
  },
): ReactElement => {
  const {
    allowImportJson,
    onSubmit,
    submitButtonText,
    initialValues,
    onFormChange,
    task,
    children,
  } = props;
  const [wizardState, setWizardState] = useState<WizardState>({
    page: 0,
  });
  const { page } = wizardState;
  const activePage: ReactElement<WizardPageProps<T>> = Children.toArray(children)[
    page
  ] as ReactElement<WizardPageProps<T>>;
  const length = Children.count(children);
  const isLastPage = page === length - 1;

  const next = (values: T): void => {
    setWizardState((wizardState) => ({
      page: Math.min(wizardState.page + 1, length - 1),
      values,
    }));
  };

  const previous = (values: T): void => {
    setWizardState((wizardState) => ({
      page: Math.max(wizardState.page - 1, 0),
      values,
    }));
  };

  const validate = (values: T): ValidationErrors | Promise<ValidationErrors> | undefined =>
    activePage.props.validate ? activePage.props.validate(values) : {};

  const handleSubmit = (values: T): void => {
    if (isLastPage) {
      onSubmit?.(values);
    } else {
      next(values);
    }
  };

  return (
    <Form
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmit}
      mutators={{
        ...arrayMutators,
      }}
    >
      {(renderProps: FormRenderProps<T>): ReactElement => (
        <StyledForm onSubmit={renderProps.handleSubmit} autoComplete={'off'}>
          {!isNil(onFormChange) && <FormSpy onChange={onFormChange} />}
          <Container>
            {allowImportJson && (
              <ImportJson
                onDrop={(importedValues: T): void =>
                  renderProps.form.reset(
                    mergeWith({}, initialValues, importedValues, (objValue, srcValue) => {
                      if (isArray(objValue) && isArray(srcValue)) {
                        // Take the first item of an array is a basis for merge
                        for (let i = 0; i < srcValue.length; i++) {
                          srcValue[i] = merge({}, objValue[0], srcValue[i]);
                        }
                      }
                      //  If customizer returns undefined, merging is handled by the method instead
                      return isNilOrEmpty(srcValue) ? objValue : undefined;
                    }),
                  )
                }
              />
            )}
            <ActivePage>
              {React.cloneElement(activePage, {
                formRenderProps: renderProps,
              })}
            </ActivePage>
            <Buttons page={page}>
              {page > 0 && (
                <ArrowLeftButton
                  type={ButtonType.BUTTON}
                  onClick={(): void => {
                    previous(renderProps.values);
                  }}
                />
              )}
              {!isLastPage && <ArrowRightButton type={ButtonType.SUBMIT} />}
              {isLastPage && (
                <FormButton
                  isInvalid={renderProps.invalid}
                  disabled={renderProps.submitting}
                  task={task}
                >
                  {submitButtonText}
                </FormButton>
              )}
            </Buttons>
          </Container>
        </StyledForm>
      )}
    </Form>
  );
};

const Container = styled(Column)`
  flex: 1 1 auto;
  justify-content: space-between;
`;

const StyledForm = styled.form`
  display: flex;
  flex: 1 1 auto;
`;

const ActivePage = styled(Column)`
  flex: 1 1 auto;
  width: 100%;
`;

const Buttons = styled(({ page, ...rest }) => <Box {...rest} />)<{
  page: number;
}>`
  width: 100%;
  padding: 40px 0;
  align-items: center;

  ${(props): FlattenSimpleInterpolation =>
    props.page === 0
      ? css`
          justify-content: flex-end;
        `
      : css`
          justify-content: space-between;
        `};
`;



// WEBPACK FOOTER //
// ./view/components/ui/form/wizard.component.tsx